/* eslint-disable react/jsx-filename-extension, import/prefer-default-export */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import history from '../../../services/history';

import api from '../../../config/configApi';

import { Context } from '../../../Context/AuthContext';
import { PicosManhaes, NomeManhaes } from './LogoMarcaManhaes';
import useHoraBrasilia, { faseDoCeu } from './useHoraBrasilia';
import './login.css';

const TEMPO_PARA_DESCANSO_MS = 60000;

export function Login() {
  const { state } = useLocation();

  const { signIn } = useContext(Context);
  const agora = useHoraBrasilia();
  const emailRef = useRef(null);
  const senhaRef = useRef(null);
  // Quem chega com uma mensagem (ex: senha redefinida) vai direto para o login.
  const [entrando, setEntrando] = useState(Boolean(state?.mensagem));

  const [user, setUser] = useState({
    email: '',
    password: '',
  });

  const [status, setStatus] = useState({
    type: state ? state.type : '',
    mensagem: state ? state.mensagem : '',
    loading: false,
  });

  const valorInput = (e) => setUser({ ...user, [e.target.name]: e.target.value });

  const loginSubmit = async (e) => {
    e.preventDefault();
    // console.log(user.password);
    setStatus({
      loading: true,
    });

    const headers = {
      'Content-Type': 'application/json',
    };

    await api.post('/login/login', user, { headers })
      .then((response) => {
        /* console.log(response); */
        setStatus({
          /* type: 'success',
                    mensagem: response.data.mensagem, */
          loading: false,
        });
        const decodedToken = jwtDecode(response.data.token);
        const departments = decodedToken?.departments;
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        localStorage.setItem('name', response.data.user.nome);
        localStorage.setItem('email', user.email);
        localStorage.setItem('image', response.data.user.image);
        localStorage.setItem('empresaId', response.data.user.empresaId);
        localStorage.setItem('equipeId', response.data.user.equipeId);
        localStorage.setItem('matricula', response.data.user.matricula);
        localStorage.setItem('perfilId', response.data.user.perfilId);
        localStorage.setItem('urlfoto', response.data.user.urlfoto);
        localStorage.setItem('departments', departments);
        return api.get('/profile/permissoes', { headers: { Authorization: `Bearer ${response.data.token}` } })
          .then((permissoesResponse) => {
            localStorage.setItem('permissoesAdmin', permissoesResponse.data.admin ? 'true' : 'false');
            localStorage.setItem('permissoes', JSON.stringify(permissoesResponse.data.chaves || []));
          })
          .catch(() => {
            localStorage.setItem('permissoesAdmin', 'false');
            localStorage.setItem('permissoes', '[]');
          });
      }).then(() => {
        signIn(true);
        return history.push('/meu-dia');
      }).catch((err) => {
        if (err.response) {
          // console.log(err.response);
          setStatus({
            type: 'error',
            mensagem: err.response.data.mensagem,
            loading: false,
          });
        } else {
          // console.log("Erro: tente mais tarde");
          setStatus({
            type: 'error',
            mensagem: 'Erro: tente mais tarde!',
            loading: false,
          });
        }
      });
  };

  // Lê os campos de verdade (não só o estado): o preenchimento automático do
  // navegador põe e-mail/senha nos campos sem disparar onChange.
  const formularioVazio = () => !emailRef.current?.value && !senhaRef.current?.value;

  // Qualquer tecla/clique/toque sai do descanso; Esc (com o formulário vazio) volta.
  useEffect(() => {
    const aoTeclar = (e) => {
      if (!entrando) {
        setEntrando(true);
        return;
      }
      if (e.key === 'Escape' && formularioVazio() && !status.loading) setEntrando(false);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [entrando, status.loading]);

  // Parado no login, com o formulário vazio, volta ao descanso.
  useEffect(() => {
    if (!entrando || status.loading || status.type) return undefined;
    const talvezDescansar = () => { if (formularioVazio()) setEntrando(false); };
    let timer = setTimeout(talvezDescansar, TEMPO_PARA_DESCANSO_MS);
    const reiniciar = () => {
      clearTimeout(timer);
      timer = setTimeout(talvezDescansar, TEMPO_PARA_DESCANSO_MS);
    };
    window.addEventListener('pointermove', reiniciar);
    window.addEventListener('keydown', reiniciar);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointermove', reiniciar);
      window.removeEventListener('keydown', reiniciar);
    };
  }, [entrando, status.loading, status.type]);

  useEffect(() => {
    if (!entrando) return undefined;
    const t = setTimeout(() => emailRef.current?.focus(), 300);
    return () => clearTimeout(t);
  }, [entrando]);

  const fase = faseDoCeu(agora.horaDecimal);
  const digitos = (valor) => valor.split('').map((d, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={i} className="manhaes-digito">{d}</span>
  ));

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={`manhaes-login${entrando ? ' esta-entrando' : ''}`}
      data-fase={fase}
      onPointerDown={() => { if (!entrando) setEntrando(true); }}
    >
      <div className="manhaes-relogio" role="timer" aria-label={`Horário de Brasília ${agora.horas}:${agora.minutos}`}>
        <div className="manhaes-hora" aria-hidden="true">
          {digitos(agora.horas)}
          <span className="manhaes-separador">:</span>
          {digitos(agora.minutos)}
          <span className="manhaes-segundos">{agora.segundos}</span>
        </div>
        <p className="manhaes-data">{`${agora.dataExtenso} · horário de Brasília`}</p>
      </div>

      <p className="manhaes-convite">Toque ou pressione qualquer tecla para entrar</p>

      <div className="manhaes-serra">
        <PicosManhaes idPrefixo="serra" />
      </div>

      <NomeManhaes className="manhaes-assinatura" />

      <section className="manhaes-painel" aria-hidden={!entrando}>
        <PicosManhaes className="manhaes-picos-painel" idPrefixo="painel" />
        <h1>Acesso ao sistema</h1>
        <form onSubmit={loginSubmit} noValidate>
          {status.type === 'error' && <p className="manhaes-aviso erro" role="alert">{status.mensagem}</p>}
          {status.type === 'success' && <p className="manhaes-aviso sucesso" role="status">{status.mensagem}</p>}

          <label className="manhaes-campo" htmlFor="login-email">
            E-mail
            <input
              ref={emailRef}
              id="login-email"
              type="email"
              name="email"
              autoComplete="username"
              onChange={valorInput}
            />
          </label>
          <label className="manhaes-campo" htmlFor="login-senha">
            Senha
            <input
              ref={senhaRef}
              id="login-senha"
              type="password"
              name="password"
              autoComplete="current-password"
              onChange={valorInput}
            />
          </label>

          <button type="submit" className="manhaes-entrar" disabled={status.loading}>
            {status.loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
        <button type="button" className="manhaes-voltar" onClick={() => setEntrando(false)}>
          Voltar ao relógio
        </button>
      </section>
    </div>
  );
}
